import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';

import ContentSpinner from '@/components/ContentSpinner';
import { PageHeading } from '@/components/Layout/Parts';
import Text from '@/components/Text';
import useAlerts from '@/hooks/useAlerts';
import { EditorialQueuePage } from '@/types/resources';
import { api } from '@/utils/api';
import { get } from '@/utils/storage';

const EditorialDistributionQueuePlaceholder = () => {
  const [searchParams] = useSearchParams();
  const alerts = useAlerts();

  const activePage = get<{
    id: string;
    status: string;
    network?: string;
  }>(`editorial_distribution_queue_active_page`);

  const { data } = useQuery(
    ['editorial_distribution_queue_channels'],
    () => api.get<never, EditorialQueuePage[]>('/v1/editorial/queue/channels'),
    {
      staleTime: 120_000,
      enabled: !activePage,
    },
  );

  useEffect(() => {
    const status = searchParams.get('authorization_status');

    if (status === 'success') {
      alerts.success('Authorization successful!');

      return;
    }

    if (status === 'error') {
      alerts.error('Failed to authorize!');
    }
  }, [searchParams, alerts]);

  if (activePage) {
    return (
      <Navigate
        to={`./${activePage.id}/${activePage.status}/${activePage.network ?? 'facebook'}`}
        relative="path"
        replace
      />
    );
  }

  if (data?.[0]) {
    return (
      <Navigate
        to={`./${data[0].id}/queued/facebook`}
        relative="path"
        replace
      />
    );
  }

  return (
    <>
      <Text $type="caption" $color="gray">
        EDITORIAL
      </Text>
      <PageHeading>Distribution Queue</PageHeading>
      <ContentSpinner />
    </>
  );
};

export default EditorialDistributionQueuePlaceholder;
